import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminAPI, authAPI } from '../api';
import { Check, AlertCircle, MessageCircle, Settings, Lock, DollarSign } from 'react-feather';
import { getAvailableCurrencies, getCurrencyInfo } from '../utils/currency';
import './AdminSettings.css';

const CURRENCIES = getAvailableCurrencies();
const ADMIN_PERMISSION_KEYS = [
  'admin_can_manage_users',
  'admin_can_manage_balances',
  'admin_can_manage_roles',
  'admin_can_manage_settings',
  'admin_can_manage_services',
  'admin_can_manage_orders',
  'admin_can_manage_deposits',
  'admin_can_manage_support',
  'admin_can_manage_service_testing',
];

const ADMIN_PERMISSION_LABELS = [
  ['admin_can_manage_users', 'Edit user status', 'Allow regular admins to activate or ban users.'],
  ['admin_can_manage_balances', 'Edit balances', 'Allow regular admins to credit or debit user balances.'],
  ['admin_can_manage_roles', 'Edit user roles', 'Allow regular admins to switch users between user and admin.'],
  ['admin_can_manage_settings', 'Edit panel settings', 'Allow regular admins to change pricing, bonuses, support, reset, and gateway settings.'],
  ['admin_can_manage_services', 'Edit services', 'Allow regular admins to sync services and update service pricing or status.'],
  ['admin_can_manage_orders', 'Manage orders', 'Allow regular admins to refund orders.'],
  ['admin_can_manage_deposits', 'Manage deposits', 'Allow regular admins to verify deposits.'],
  ['admin_can_manage_support', 'Manage support', 'Allow regular admins to view and resolve user complaints.'],
  ['admin_can_manage_service_testing', 'Manage service testing', 'Allow regular admins to assign test users and approve new services.'],
];

export default function AdminSettings() {
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState({
    profit_multiplier: '1.3',
    virtual_numbers_multiplier: '1.3',
    virtual_numbers_status: 'beta',
    deposit_rate_markup_percent: '0',
    whatsapp_number: '',
    site_name: 'NiceBooster',
    min_deposit: '5',
    max_deposit: '5000',
    reset_password_email_enabled: '1',
    reset_password_code_enabled: '1',
    first_deposit_bonus_enabled: '1',
    first_deposit_bonus_percent: '10',
    referral_program_enabled: '1',
    referral_commission_percent: '5',
    referral_invitee_discount_percent: '10',
    referral_invitee_discount_order_limit: '5',
    registration_bonus_enabled: '0',
    registration_bonus_amount: '0',
    registration_bonus_currency: 'USD',
    fapshi_enabled: '1',
    monetbil_enabled: '1',
    helaket_enabled: '0',
    swychr_enabled: '0',
    admin_can_manage_users: '1',
    admin_can_manage_balances: '1',
    admin_can_manage_roles: '1',
    admin_can_manage_settings: '1',
    admin_can_manage_services: '1',
    admin_can_manage_orders: '1',
    admin_can_manage_deposits: '1',
    admin_can_manage_support: '1',
    deposit_rate_mode: 'live',
    deposit_rate_fixed_xaf: '655.957',
  });
  const [currency, setCurrency] = useState(user?.currency || 'USD');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');

  useEffect(() => {
    adminAPI.settings().then(r => {
      setSettings(prev => ({ ...prev, ...r.data.settings }));
    }).finally(() => setLoading(false));
    if (user?.currency) setCurrency(getCurrencyInfo(user.currency) ? user.currency : 'USD');
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setMsg('');
    if (settings.reset_password_email_enabled === '0' && settings.reset_password_code_enabled === '0') {
      setMsg('Enable at least one password reset method.');
      setSaving(false);
      return;
    }
    try {
      const nextSettings = {
        ...settings,
        registration_bonus_currency: currency,
      };

      if (user?.role !== 'super_admin') {
        ADMIN_PERMISSION_KEYS.forEach((key) => {
          delete nextSettings[key];
        });
      }

      await adminAPI.updateSettings(nextSettings);
      setSettings(prev => ({ ...prev, registration_bonus_currency: currency }));
      setMsg('Settings saved successfully');
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to save settings');
    } finally { setSaving(false); }
  };

  const handleCurrencyChange = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      await authAPI.updateProfile({ currency });
      setMsg('Currency updated successfully');
      await refreshUser();
    } catch {
      setMsg('Failed to update currency');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordMsg('');

    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordMsg('All fields are required');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMsg('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordMsg('New password must be at least 6 characters');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.oldPassword === passwordForm.newPassword) {
      setPasswordMsg('New password must be different from current password');
      setPasswordLoading(false);
      return;
    }

    try {
      await authAPI.changePassword({
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordMsg('Password changed successfully');
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordMsg(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const field = (key, label, type = 'text', hint = '') => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="form-input" type={type} value={settings[key] || ''}
        onChange={e => setSettings({ ...settings, [key]: e.target.value })} />
      {hint && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
    </div>
  );

  const currentCurrencyObj = getCurrencyInfo(currency);
  const isSuperAdmin = user?.role === 'super_admin';

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div className="admin-settings-page">
      <h1 className="page-title">Settings</h1>
      <p className="page-subtitle">Configure your panel and account</p>

      {/* Panel Settings Form */}
      <form className="admin-settings-form" onSubmit={handleSave} style={{ maxWidth: 560, marginBottom: 28 }}>
        {msg && (
          <div className={`alert ${msg.includes('Failed') ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            {msg.includes('Failed') ? <AlertCircle size={18} /> : <Check size={18} />}
            <span>{msg}</span>
          </div>
        )}

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 18, fontSize: 16 }}>Pricing</h3>
          {field('profit_multiplier', 'Global Profit Multiplier', 'number', 'e.g. 1.3 = 30% markup over supplier price. Applied to all services without a custom price.')}
          {field('virtual_numbers_multiplier', 'Virtual Numbers Multiplier', 'number', 'e.g. 1.3 = 30% markup over SMSPool/supplier price.')}
          
          <div className="form-group">
            <label className="form-label">Virtual Numbers Status</label>
            <select
              className="form-select"
              value={settings.virtual_numbers_status || 'beta'}
              onChange={e => setSettings({ ...settings, virtual_numbers_status: e.target.value })}
            >
              <option value="beta">Beta (Testers Only)</option>
              <option value="active">Active (All Users)</option>
              <option value="disabled">Disabled</option>
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Controls who can see and use the Virtual Numbers service.
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Deposit Exchange Rate Mode</label>
            <select
              className="form-select"
              value={settings.deposit_rate_mode || 'live'}
              onChange={e => setSettings({ ...settings, deposit_rate_mode: e.target.value })}
            >
              <option value="live">Live API Rates</option>
              <option value="fixed">Fixed Custom Rate</option>
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Live uses current market rates. Fixed allows you to lock the rate to avoid decimals (e.g., exactly 650 FCFA = 1 USD).
            </div>
          </div>
          {settings.deposit_rate_mode === 'fixed' && field('deposit_rate_fixed_xaf', 'Fixed XAF to USD Rate', 'number', 'Example: 650. Used exactly as the conversion rate.')}
          
          {field('deposit_rate_markup_percent', 'Deposit Rate Markup (%)', 'number', 'Positive values make FCFA deposits credit less USD. Negative values make them credit more USD.')}
          {field('min_deposit', 'Minimum Deposit (USD)', 'number')}
          {field('max_deposit', 'Maximum Deposit (USD)', 'number')}
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 18, fontSize: 16 }}>Bonuses</h3>

          <div className="form-group">
            <label className="form-label">First Deposit Bonus</label>
            <select
              className="form-select"
              value={settings.first_deposit_bonus_enabled || '1'}
              onChange={e => setSettings({ ...settings, first_deposit_bonus_enabled: e.target.value })}
            >
              <option value="1">Enabled</option>
              <option value="0">Disabled</option>
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Enables the welcome bonus promised on the landing page.
            </div>
          </div>

          {field('first_deposit_bonus_percent', 'First Deposit Bonus (%)', 'number', 'Example: 10 means the first completed deposit credits an extra 10%.')}

          <div className="form-group">
            <label className="form-label">Referral Program</label>
            <select
              className="form-select"
              value={settings.referral_program_enabled || '1'}
              onChange={e => setSettings({ ...settings, referral_program_enabled: e.target.value })}
            >
              <option value="1">Enabled</option>
              <option value="0">Disabled</option>
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Lets users earn commission from invited users and gives invitees discounted starter orders.
            </div>
          </div>

          {field('referral_commission_percent', 'Referral Deposit Commission (%)', 'number', 'Example: 5 means the inviter gets 5% of every completed deposit made by their invitee.')}
          {field('referral_invitee_discount_percent', 'Invitee Order Discount (%)', 'number', 'Example: 10 means invited users pay 10% less on qualifying orders.')}
          {field('referral_invitee_discount_order_limit', 'Invitee Discounted Orders Limit', 'number', 'Example: 5 means the invitee discount applies to the first 5 orders.')}

          <div className="form-group">
            <label className="form-label">Registration Bonus</label>
            <select
              className="form-select"
              value={settings.registration_bonus_enabled || '0'}
              onChange={e => setSettings({ ...settings, registration_bonus_enabled: e.target.value })}
            >
              <option value="1">Enabled</option>
              <option value="0">Disabled</option>
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Gives new users a signup bonus when they create an account.
            </div>
          </div>

          {field(
            'registration_bonus_amount',
            `Registration Bonus Amount (${currentCurrencyObj?.code || currency})`,
            'number',
            `Current save currency: ${settings.registration_bonus_currency || currency}. Saving again will store it using your current admin account currency: ${currentCurrencyObj?.symbol || ''} ${currentCurrencyObj?.code || currency}.`
          )}
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 18, fontSize: 16 }}><MessageCircle size={18} style={{ marginRight: 8, display: 'inline-block', verticalAlign: 'middle' }} /> Support</h3>
          {field('whatsapp_number', 'WhatsApp Number', 'text', 'Include country code, e.g. +237600000000')}
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 18, fontSize: 16 }}><Settings size={18} style={{ marginRight: 8, display: 'inline-block', verticalAlign: 'middle' }} /> General</h3>
          {field('site_name', 'Site Name', 'text')}
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 18, fontSize: 16 }}><Lock size={18} style={{ marginRight: 8, display: 'inline-block', verticalAlign: 'middle' }} /> Password Reset Methods</h3>
          <div className="form-group">
            <label className="form-label">Email Reset Link</label>
            <select
              className="form-select"
              value={settings.reset_password_email_enabled || '1'}
              onChange={e => setSettings({ ...settings, reset_password_email_enabled: e.target.value })}
            >
              <option value="1">Enabled</option>
              <option value="0">Disabled</option>
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Sends a reset link by email when Resend or SMTP is configured.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Verification Code Reset</label>
            <select
              className="form-select"
              value={settings.reset_password_code_enabled || '1'}
              onChange={e => setSettings({ ...settings, reset_password_code_enabled: e.target.value })}
            >
              <option value="1">Enabled</option>
              <option value="0">Disabled</option>
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Shows a one-time code flow on the forgot password page. You can enable either method or both.
            </div>
          </div>

          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            At least one reset method must stay enabled.
          </div>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 18, fontSize: 16 }}>Payment Gateways</h3>

          <div className="form-group">
            <label className="form-label">Fapshi</label>
            <select
              className="form-select"
              value={settings.fapshi_enabled || '1'}
              onChange={e => setSettings({ ...settings, fapshi_enabled: e.target.value })}
            >
              <option value="1">Enabled</option>
              <option value="0">Disabled</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Monetbil</label>
            <select
              className="form-select"
              value={settings.monetbil_enabled || '1'}
              onChange={e => setSettings({ ...settings, monetbil_enabled: e.target.value })}
            >
              <option value="1">Enabled</option>
              <option value="0">Disabled</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Helaket</label>
            <select
              className="form-select"
              value={settings.helaket_enabled || '0'}
              onChange={e => setSettings({ ...settings, helaket_enabled: e.target.value })}
            >
              <option value="1">Enabled</option>
              <option value="0">Disabled</option>
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Use this when you are ready to open the crypto gateway to users.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Swychr</label>
            <select
              className="form-select"
              value={settings.swychr_enabled || '0'}
              onChange={e => setSettings({ ...settings, swychr_enabled: e.target.value })}
            >
              <option value="1">Enabled</option>
              <option value="0">Disabled</option>
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Enables hosted collection links through Swychr Connect.
            </div>
          </div>
        </div>

        {isSuperAdmin && (
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ marginBottom: 18, fontSize: 16 }}>Admin Permissions</h3>
            {ADMIN_PERMISSION_LABELS.map(([key, label, hint]) => (
              <div className="form-group" key={key}>
                <label className="form-label">{label}</label>
                <select
                  className="form-select"
                  value={settings[key] || '1'}
                  onChange={e => setSettings({ ...settings, [key]: e.target.value })}
                >
                  <option value="1">Allowed</option>
                  <option value="0">Blocked</option>
                </select>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  {hint}
                </div>
              </div>
            ))}
          </div>
        )}

        <button className="btn btn-primary btn-lg" type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Panel Settings'}
        </button>
      </form>

      {/* Account Settings */}
      <form className="admin-settings-form" onSubmit={handleCurrencyChange} style={{ maxWidth: 560, marginBottom: 28 }}>
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
            <DollarSign size={20} style={{ color: 'var(--accent)', flexShrink: 0 }} />
            <h3 style={{ fontSize: 16, margin: 0 }}>Account Currency</h3>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>
            Choose your preferred currency for displaying prices and balance.
          </p>

          <div className="form-group">
            <label className="form-label">Currency</label>
            <select
              className="form-input"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              disabled={saving}
              style={{
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {CURRENCIES.map((curr) => (
                <option key={curr.code} value={curr.code}>
                  {curr.symbol} {curr.code} - {curr.name}
                </option>
              ))}
            </select>
            {currentCurrencyObj && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                Currently selected: <strong>{currentCurrencyObj.symbol} {currentCurrencyObj.code}</strong> - {currentCurrencyObj.name}
              </div>
            )}
          </div>

          <button
            className="btn btn-primary btn-lg"
            type="submit"
            disabled={saving || currency === user?.currency}
            style={{
              opacity: saving || currency === user?.currency ? 0.6 : 1,
              cursor: saving || currency === user?.currency ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Updating...' : <><Check size={16} style={{ marginRight: 6, display: 'inline-block', verticalAlign: 'middle' }} /> Save Currency</>}
          </button>
        </div>
      </form>

      {/* Password Change Form */}
      <form className="admin-settings-form" onSubmit={handlePasswordChange} style={{ maxWidth: 560 }}>
        {passwordMsg && (
          <div className={`alert ${passwordMsg.includes('successfully') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            {passwordMsg.includes('successfully') ? <Check size={18} /> : <AlertCircle size={18} />}
            <span>{passwordMsg}</span>
          </div>
        )}

        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
            <Lock size={20} style={{ color: 'var(--accent)', flexShrink: 0 }} />
            <h3 style={{ fontSize: 16, margin: 0 }}>Change Password</h3>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>
            Update your password to keep your account secure.
          </p>

          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input
              className="form-input"
              type="password"
              value={passwordForm.oldPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
              placeholder="••••••••"
              autoComplete="current-password"
              disabled={passwordLoading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">New Password</label>
            <input
              className="form-input"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              placeholder="••••••••"
              autoComplete="new-password"
              disabled={passwordLoading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input
              className="form-input"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              placeholder="••••••••"
              autoComplete="new-password"
              disabled={passwordLoading}
            />
          </div>

          <button
            className="btn btn-primary btn-lg"
            type="submit"
            disabled={passwordLoading || !passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
            style={{
              opacity: passwordLoading || !passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword ? 0.6 : 1,
              cursor: passwordLoading || !passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword ? 'not-allowed' : 'pointer',
            }}
          >
            {passwordLoading ? 'Updating...' : <><Check size={16} style={{ marginRight: 6, display: 'inline-block', verticalAlign: 'middle' }} /> Change Password</>}
          </button>
        </div>
      </form>
    </div>
  );
}
